import { useCallback, useMemo } from "react";
import { useAppSelector } from "@hooks/redux";
import { selectCreateOptions } from "./videoCreatingSlice";

export function useVideoModelRules() {
  const { rules, modelOptions, resolutionOptions } = useAppSelector(selectCreateOptions);

  /** duration → 允许的 resolution 列表（筛选后的 option 对象） */
  const getFilteredResolutionOptions = useCallback(
    (duration: number | string) => {
      const allowed = rules?.duration_resolution?.[duration];
      if (!allowed?.length) return resolutionOptions;
      return resolutionOptions.filter((opt) => allowed.includes(opt.value));
    },
    [rules, resolutionOptions],
  );

  /** duration → 允许的 model 列表（筛选后的 option 对象） */
  const getFilteredModelOptions = useCallback(
    (duration: number | string) => {
      const allowed = rules?.duration_model?.[duration];
      if (!allowed?.length) return [];
      return modelOptions.filter((opt) => allowed.includes(opt.value));
    },
    [rules, modelOptions],
  );

  /** 检查某 resolution 在指定 duration 下是否允许 */
  const isResolutionAllowed = useCallback(
    (duration: number | string, resolution: string) => {
      const allowed = rules?.duration_resolution?.[duration];
      if (!allowed?.length) return true;
      return allowed.includes(resolution);
    },
    [rules],
  );

  /** 检查某 model 在指定 duration 下是否允许 */
  const isModelAllowed = useCallback(
    (duration: number | string, model: string) => {
      const allowed = rules?.duration_model?.[duration];
      if (!allowed?.length) return false;
      return allowed.includes(model);
    },
    [rules],
  );

  /** 获取指定 duration 下的默认（第一个）resolution */
  const getDefaultResolution = useCallback(
    (duration: number | string): string | undefined => {
      const allowed = rules?.duration_resolution?.[duration];
      return allowed?.[0] as string | undefined;
    },
    [rules],
  );

  /** 获取指定 duration 下的默认（第一个）model */
  const getDefaultModel = useCallback(
    (duration: number | string): string | undefined => {
      const allowed = rules?.duration_model?.[duration];
      return allowed?.[0];
    },
    [rules],
  );

  /**
   * duration 变更时，修正关联的 resolution 和 model。
   * 返回需要更新的字段（不含 duration 本身）。
   */
  const correctParamsForDuration = useCallback(
    (newDuration: number | string, currentResolution: string, currentModel: string) => {
      const updates: { resolution?: string; internalModelId?: string } = {};

      const allowedResolutions = rules?.duration_resolution?.[newDuration];
      if (allowedResolutions?.length) {
        if (!allowedResolutions.includes(currentResolution)) {
          updates.resolution = allowedResolutions[0] as string;
        }
      }

      const allowedModels = rules?.duration_model?.[newDuration];
      if (allowedModels?.length) {
        if (!allowedModels.includes(currentModel)) {
          updates.internalModelId = allowedModels[0];
        }
      }

      return updates;
    },
    [rules],
  );

  /**
   * resolution 变更时，修正关联的 duration。
   * duration_resolution 是双向映射，以 resolution 为 key 可查允许的 duration。
   * 返回需要更新的字段（不含 resolution 本身）。
   */
  const correctDurationForResolution = useCallback(
    (newResolution: string, currentDuration: number | string) => {
      const updates: { duration?: number } = {};
      const allowed = rules?.duration_resolution?.[newResolution];
      if (allowed?.length && !allowed.includes(currentDuration)) {
        updates.duration = allowed[0] as number;
      }
      return updates;
    },
    [rules],
  );

  /** 当前 rules 是否已加载（非空） */
  const hasRules = useMemo(
    () =>
      Boolean(
        rules &&
        (Object.keys(rules.duration_resolution ?? {}).length || Object.keys(rules.duration_model ?? {}).length),
      ),
    [rules],
  );

  return {
    hasRules,
    getFilteredResolutionOptions,
    getFilteredModelOptions,
    isResolutionAllowed,
    isModelAllowed,
    getDefaultResolution,
    getDefaultModel,
    correctParamsForDuration,
    correctDurationForResolution,
  };
}
